import { User, CreatorProfile } from '@/types';
import { supabase } from '../supabase/client';
import { authMonitor } from './monitoring';
import { traceAuthFlow } from './debug';
import { getAuthManager } from './get-auth-manager';

interface ProfileCache {
  user: User | null;
  creatorProfile: CreatorProfile | null;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

/**
 * Profile Manager - Handles user profile data separately from auth
 * Features:
 * - Lazy loading (only fetch when needed)
 * - Caching with TTL
 * - Graceful error handling
 * - Works independently of auth state
 */
export class ProfileManager {
  private cache: ProfileCache = {
    user: null,
    creatorProfile: null,
    timestamp: 0,
    ttl: 5 * 60 * 1000 // 5 minutes
  };
  
  private loading = false;
  private error: Error | null = null;
  private listeners = new Set<(user: User | null) => void>();

  constructor() {
    // Initialization is handled in init-auth-system.ts to avoid circular dependencies
  }

  /**
   * Get user profile - uses cache if available and fresh
   */
  async getProfile(forceRefresh = false): Promise<{ user: User | null; error: Error | null }> {
    try {
      // Check if we have a valid cached profile
      if (!forceRefresh && this.isCacheValid()) {
        traceAuthFlow('ProfileManager.getProfile', { source: 'cache' });
        return { user: this.cache.user, error: null };
      }

      // Get current auth session using lazy-loaded auth manager
      const authManager = await getAuthManager();
      const session = authManager.getSession();
      if (!session) {
        return { user: null, error: null }; // Not an error, just not authenticated
      }

      // Fetch profile
      authMonitor.startTimer('profileFetch');
      traceAuthFlow('ProfileManager.getProfile', { source: 'database', userId: session.user.id });
      
      this.loading = true;
      this.error = null;

      const { data: profile, error } = await supabase
        .from('users')
        .select(`
          *,
          creator_profile:creator_profiles(*)
        `)
        .eq('id', session.user.id)
        .single();

      if (error) {
        authMonitor.endTimer('profileFetch', false, error);
        this.error = error;
        this.loading = false;
        
        // Return cached data if available, even if stale
        if (this.cache.user) {
          console.warn('Profile fetch failed, using stale cache:', error);
          return { user: this.cache.user, error };
        }
        
        return { user: null, error };
      }

      // Map the profile data
      const mappedUser: User = {
        ...profile,
        status: profile.status || 'active'
      };
      
      // Handle creator_profile which might be an array from the query
      if (profile.creator_profile) {
        if (Array.isArray(profile.creator_profile)) {
          mappedUser.creator_profile = profile.creator_profile.length > 0 ? profile.creator_profile[0] : null;
        } else {
          mappedUser.creator_profile = profile.creator_profile;
        }
      } else {
        mappedUser.creator_profile = null;
      }

      // Update cache
      this.cache = {
        user: mappedUser,
        creatorProfile: mappedUser.creator_profile || null,
        timestamp: Date.now(),
        ttl: this.cache.ttl
      };

      this.loading = false;
      authMonitor.endTimer('profileFetch', true);
      
      // Notify listeners
      this.notifyListeners(mappedUser);

      return { user: mappedUser, error: null };

    } catch (error) {
      authMonitor.endTimer('profileFetch', false, error as Error);
      this.error = error as Error;
      this.loading = false;
      
      // Return cached data if available
      if (this.cache.user) {
        return { user: this.cache.user, error: error as Error };
      }
      
      return { user: null, error: error as Error };
    }
  }

  /**
   * Get just the role - extracted from cached user or fetched if needed
   */
  async getRole(): Promise<'user' | 'creator' | 'admin' | null> {
    const { user } = await this.getProfile();
    return user?.role || null;
  }

  /**
   * Update profile - invalidates cache
   */
  async updateProfile(updates: Partial<User>): Promise<{ user: User | null; error: Error | null }> {
    try {
      const authManager = await getAuthManager();
      const session = authManager.getSession();
      if (!session) {
        return { user: null, error: new Error('Not authenticated') };
      }

      traceAuthFlow('ProfileManager.updateProfile', { updates });

      const { data: updatedUser, error } = await supabase
        .from('users')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id)
        .select(`
          *,
          creator_profile:creator_profiles(*)
        `)
        .single();

      if (error) {
        return { user: null, error };
      }

      // Clear cache to force refresh
      this.clearCache();

      // Fetch fresh profile
      return this.getProfile(true);

    } catch (error) {
      return { user: null, error: error as Error };
    }
  }

  /**
   * Check if cache is still valid
   */
  private isCacheValid(): boolean {
    if (!this.cache.user) return false;
    
    const age = Date.now() - this.cache.timestamp;
    return age < this.cache.ttl;
  }

  /**
   * Clear the cache
   */
  clearCache() {
    traceAuthFlow('ProfileManager.clearCache');
    this.cache = {
      user: null,
      creatorProfile: null,
      timestamp: 0,
      ttl: this.cache.ttl
    };
    this.notifyListeners(null);
  }

  /**
   * Get loading state
   */
  isLoading(): boolean {
    return this.loading;
  }

  /**
   * Get error state
   */
  getError(): Error | null {
    return this.error;
  }

  /**
   * Subscribe to profile changes
   */
  subscribe(listener: (user: User | null) => void): () => void {
    this.listeners.add(listener);
    
    // Immediately call with cached data if available
    if (this.cache.user) {
      listener(this.cache.user);
    }
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Set cache TTL (for testing/configuration)
   */
  setCacheTTL(ttl: number) {
    this.cache.ttl = ttl;
  }

  private notifyListeners(user: User | null) {
    this.listeners.forEach(listener => {
      try {
        listener(user);
      } catch (error) {
        console.error('Profile listener error:', error);
      }
    });
  }
}

// Singleton instance
export const profileManager = new ProfileManager();

// Export for debugging
if (typeof window !== 'undefined') {
  (window as any).profileManager = profileManager;
} 